// app-legacy(v3) 엔드포인트 SSOT. 경로는 base 변수 없이 `/api/v3` 리터럴 반복(변수 우회 제거).
//
// 엔드포인트는 경로뿐 아니라 **자기 transport 정책까지 함께 선언**한다. 인터셉터가 별도 상수 목록
// (NO_AUTH·NO_RETRY)을 두고 URL 을 `endsWith` 매칭하던 방식을 대체 — URL 매칭은 동적 경로(`/plants/:id`)를
// 다루지 못하고, 정책이 엔드포인트 정의에서 떨어져 있어 추가할 때 두 곳을 봐야 했다. 정책은 요청 config 에
// 실려 인터셉터까지 그대로 전달된다(`http.ts`).
//
// 팩토리·기본값 없이 **모든 엔드포인트가 두 플래그를 직접 명시**한다 — 한 줄만 읽어도 그 엔드포인트의
// 인증·재시도 동작이 다 보이게(기본값을 기억해야 읽히는 표기 회피).

/** 엔드포인트 = 경로 + transport 정책. `http.get/post/put` 이 이 객체를 그대로 받는다. */
export interface Endpoint {
  path: string;
  /**
   * `Authorization: Bearer <access>` 자동 부여 여부.
   * false = 인터셉터가 헤더에 **손대지 않는다** — 무인증(login)이거나 호출처가 헤더를 소유(refresh=refresh
   * 토큰 bearer / logout=teardown 후 access 스냅샷)하는 경우. 지우지도 않는다(caller 헤더 보존).
   */
  requiresAuth: boolean;
  /**
   * 401 을 받았을 때 access 재발급 후 **1회 재시도**할지 여부.
   * false = 이 엔드포인트의 401 은 "세션 만료"가 아니라 "입력 틀림 / 비즈니스 거부" — 재발급해도 결과가
   * 같으므로 재시도가 무의미하고, 재-401 로 강제 로그아웃까지 유발하면 안 된다(호출처가 401 을 직접 해석).
   */
  retryAfterTokenRefresh: boolean;
}

export const endpoints = {
  auth: {
    // 무인증(잔재 토큰 무시). 401 = 계정 없음 / 비번 틀림.
    login: {
      path: '/api/v3/auth/login',
      requiresAuth: false,
      retryAfterTokenRefresh: false,
    },
    // 헤더 = refresh 토큰 bearer(composition 의 공통 refreshToken 이 주입).
    // 401 = refresh 만료 → 그 catch 가 null 반환으로 처리.
    refresh: {
      path: '/api/v3/auth/refresh',
      requiresAuth: false,
      retryAfterTokenRefresh: false,
    },
    // 헤더 = teardown 전 access 스냅샷(store 는 이미 비워진 뒤 호출). 401 = 세션 이미 종료.
    logout: {
      path: '/api/v3/auth/logout',
      requiresAuth: false,
      retryAfterTokenRefresh: false,
    },
    // FCM 디바이스 토큰 등록 — **특례 없는 일반 인증 엔드포인트**. 401 은 통상적 access 만료이므로
    // 재발급 후 재시도가 정상 동작이다(과거 `false` 로 제외했다가 콜드 스타트 등록 유실을 겪음 —
    // `docs/API-ISSUES.md` "FCM 토큰 등록 유실"). 재발급 실패가 곧 로그아웃이던 구조가 해소돼
    // (composition 이 사유를 보고 4xx 거절일 때만 종료) 더는 배경 요청이 세션을 끊지 않는다.
    fcm: {
      path: '/api/v3/auth/fcm',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  user: {
    // 401 = 현재 비번 틀림 (BE 시맨틱).
    passwordVerify: {
      path: '/api/v3/users/me/password/verify',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    password: {
      path: '/api/v3/users/me/password',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    // 401 = 인증코드 틀림.
    mobileVerify: {
      path: '/api/v3/users/me/mobile/verify',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    mobile: {
      path: '/api/v3/users/me/mobile',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    profile: {
      path: '/api/v3/users/me/profile',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    consents: {
      path: '/api/v3/users/me/consents',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    alarm: {
      path: '/api/v3/users/me/alarm',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  plant: {
    base: {
      path: '/api/v3/plants',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    byId: (plantId: number): Endpoint => ({
      path: `/api/v3/plants/${plantId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    profile: (plantId: number): Endpoint => ({
      path: `/api/v3/plants/${plantId}/profile`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  inverter: {
    base: {
      path: '/api/v3/feature/plants/inverters',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    byId: (inverterId: number): Endpoint => ({
      path: `/api/v3/feature/plants/inverters/${inverterId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    byPlant: (plantId: number): Endpoint => ({
      path: `/api/v3/feature/plants/${plantId}/inverters`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    byPlantAndId: (plantId: number, inverterId: number): Endpoint => ({
      path: `/api/v3/feature/plants/${plantId}/inverters/${inverterId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  rtu: {
    base: {
      path: '/api/v3/feature/plants/rtus',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    byId: (rtuId: number): Endpoint => ({
      path: `/api/v3/feature/plants/rtus/${rtuId}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    byPlant: (plantId: number): Endpoint => ({
      path: `/api/v3/feature/plants/${plantId}/rtus`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  statistics: {
    hourly: {
      path: '/api/v3/statistics/hourly',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    daily: {
      path: '/api/v3/statistics/daily',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    monthly: {
      path: '/api/v3/statistics/monthly',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    range: {
      path: '/api/v3/statistics/range',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  event: {
    base: {
      path: '/api/v3/events',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  code: {
    uses: {
      path: '/api/v3/code/uses',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    contractsExport: {
      path: '/api/v3/code/contracts/export',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
    contractsSelf: {
      path: '/api/v3/code/contracts/self',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    },
  },

  consent: {
    /** 약관 목록 — 공개 엔드포인트(BE 인증 해제). 비로그인 진입(고객센터·가입 동의)에서도 그대로 호출한다. */
    base: {
      path: '/api/v3/consents',
      requiresAuth: false,
      retryAfterTokenRefresh: false,
    },
  },

  weather: {
    climate: (period: 'hourly' | 'daily' | 'monthly', start?: string, end?: string): Endpoint => {
      let path = `/api/v3/weather/climate/${period}`;
      if (start) path += `/${start}`;
      if (end) path += `/${end}`;
      return { path, requiresAuth: true, retryAfterTokenRefresh: true };
    },
    forecast: (start?: string, end?: string): Endpoint => {
      let path = '/api/v3/weather/forecast';
      if (start) path += `/${start}`;
      if (end) path += `/${end}`;
      return { path, requiresAuth: true, retryAfterTokenRefresh: true };
    },
    sun: (start?: string, end?: string): Endpoint => {
      let path = '/api/v3/weather/sun';
      if (start) path += `/${start}`;
      if (end) path += `/${end}`;
      return { path, requiresAuth: true, retryAfterTokenRefresh: true };
    },
  },

  kpx: {
    rec: (start?: string, end?: string): Endpoint => ({
      path: start && end ? `/api/v3/kpx/rec/${start}/${end}` : '/api/v3/kpx/rec',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
    smp: (start?: string, end?: string): Endpoint => ({
      path: start && end ? `/api/v3/kpx/smp/${start}/${end}` : '/api/v3/kpx/smp',
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  report: {
    daily: (date: string): Endpoint => ({
      path: `/api/v3/reports/daily/${date}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },

  revenue: {
    byPeriod: (period: 'hourly' | 'daily' | 'monthly', start: string, end: string): Endpoint => ({
      path: `/api/v3/revenues/${period}/${start}/${end}`,
      requiresAuth: true,
      retryAfterTokenRefresh: true,
    }),
  },
} as const;
